import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../zodios/client";
import type { Insight } from "../zodios/apiDefinitions/api/insights";

interface UseInsightsOptions {
  dismissed?: "true" | "false";
  viewed?: "true" | "false";
  entityType?: string;
  entityId?: number;
  marketplace?: string;
  status?: "PENDING" | "SUCCESS" | "FAILED" | "CANCELLED" | "RETRY" | "IN_PROGRESS" | "REJECTED";
  page?: number;
  pageSize?: number;
}

interface MockInsightData extends Insight {
  metadata: {
    result: {
      info: Array<{
        status: "info" | "success" | "warning";
        content: string;
        metrics?: Array<{
          id?: number;
          type?: string;
          text?: string;
          acos?: number;
          acos_delta?: number;
          spend?: number;
          spend_delta?: number;
          ad_spend?: number;
          ad_sales?: number;
          ad_sales_delta?: number;
          click_thru_rate?: number;
          click_thru_rate_delta?: number;
          conv_rate?: number;
          conv_rate_delta?: number;
          conversions?: number;
          cost?: number;
          cost_per_click?: number;
          cost_per_click_delta?: number;
          serving_hours?: number;
          serving_hours_delta?: number;
          target_acos_max?: number;
          target_acos_min?: number;
          query?: string;
        }>;
      }>;
      title?: string;
      status?: string;
    };
  };
}

const mockInsights: MockInsightData[] = [
  {
    id: 1,
    title: "48h Check-in: Campaign X",
    description: "Spend $312, ACOS 28.4% vs 25% target",
    entity_type: "gvadvertisement",
    entity_id: 8421,
    is_viewed: false,
    is_dismissed: false,
    created_on: new Date().toISOString(),
    status: "SUCCESS",
    metadata: {
      result: {
        info: [
          {
            status: "info",
            content: "48h performance check for Campaign X",
            metrics: [
              {
                id: 8421,
                type: "advertisement",
                spend: 312,
                acos: 0.284,
                acos_delta: 0.034,
                target_acos_max: 0.25,
                click_thru_rate: 0.0074,
                click_thru_rate_delta: -0.0018,
              },
            ],
          },
        ],
      },
    },
  },
  {
    id: 2,
    title: "Budget Raised Yesterday",
    description: "New budget $500; spent $412 (+$68)",
    entity_type: "gvadvertisement",
    entity_id: 7335,
    is_viewed: false,
    is_dismissed: false,
    created_on: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: "SUCCESS",
    metadata: {
      result: {
        info: [
          {
            status: "success",
            content: "Budget successfully raised to $500/day",
            metrics: [
              {
                id: 7335,
                type: "advertisement",
                spend: 412,
                spend_delta: 68,
              },
            ],
          },
        ],
      },
    },
  },
  {
    id: 3,
    title: "Keyword 0 conv. after 150 clicks",
    description: "Cost $62, CTR 1.2%, CVR 0.0%",
    entity_type: "keyword",
    entity_id: 11290,
    is_viewed: false,
    is_dismissed: false,
    created_on: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    status: "PENDING",
    metadata: {
      result: {
        info: [
          {
            status: "warning",
            content: "Keyword has 0 conversions after 150 clicks",
            metrics: [
              {
                id: 11290,
                type: "keyword",
                cost: 62,
                click_thru_rate: 0.012,
                conv_rate: 0,
                conversions: 0,
              },
            ],
          },
        ],
      },
    },
  },
];

export const useInsights = (options?: UseInsightsOptions) => {
  return useQuery({
    queryKey: ["insights", options],
    queryFn: async () => {
      try {
        const response = await apiClient.listInsights({
          queries: {
            dismissed: options?.dismissed || "false",
            viewed: options?.viewed,
            entity_type: options?.entityType,
            entity_id: options?.entityId,
            marketplace: options?.marketplace,
            status: options?.status,
            page: options?.page || 1,
            page_size: options?.pageSize || 20,
          },
        });
        return response;
      } catch (error) {
        // oxlint-disable-next-line no-console
        console.log("Using mock insights data due to API error:", error);
        return {
          count: mockInsights.length,
          next: null,
          previous: null,
          results: mockInsights,
        };
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useMarkInsightViewed = () => {
  return async (insightId: number) => {
    try {
      await apiClient.markInsightAsViewed(undefined, { params: { id: insightId } });
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.error("Failed to mark insight as viewed:", error);
    }
  };
};

export const useDismissInsight = () => {
  return async (insightId: number) => {
    try {
      await apiClient.dismissInsight(undefined, { params: { id: insightId } });
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.error("Failed to dismiss insight:", error);
    }
  };
};
